// CreatorChannelsPanel.tsx — chaînes d'un créateur, dans l'onglet Ressources de sa fiche
//
// Même présentation que l'onglet « Chaînes » du Dashboard (barre de filtres, bascule
// cartes / liste, mêmes cartes, même liste), limitée aux chaînes du créateur. « Regarder »
// lance le direct (ou affiche « Est arrêtée »), comme au Dashboard.
// Données : admin-api DashboardLatest, paramètres ownerId + only=channels.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

import styles from './ChannelsSection.module.scss';
import extra  from './VideosSection.module.scss';
import dash   from './DashboardV2.module.scss';
import { Segment, ViewToggle, useView, plural, creatorPlace } from './dashboardShared';
import { DashboardChannel, ChannelOrigin, ModerationOutcome, ON_STATUSES, ORIGIN_LABEL, Thumb, Badge, Actions, useChannelModeration, stateOf, isNew, placeOf,
         dayLabel, hour, tintOf } from './ChannelsSection';
import VideoPlayer from '../../widget/players/VideoPlayer';
import { useTypedSelector } from '../../../store/useTypeSelector';
import { RootState } from '../../../store/generalReducer';
import { stopChannel, lockChannel, unlockChannel } from '../../../backendServices/broadcastingManagment/api_channelModeration';
import { API_URLS, WWW_URLS, WWW_DOMAIN } from '../../../backendServices/urls';

const VIEW_KEY = 'adminResources.channelsView';

type Sort = 'recent' | 'on' | 'name';
const SORTS: readonly (readonly [Sort, string])[] = [ [ 'recent', 'Plus récentes' ], [ 'on', 'Allumées d’abord' ], [ 'name', 'Nom (A → Z)' ] ];
const byDateDesc = (a: DashboardChannel, b: DashboardChannel) => (b.creationDate ?? '').localeCompare(a.creationDate ?? '');
const COMPARE: Record<Sort, (a: DashboardChannel, b: DashboardChannel) => number> = {
  recent: byDateDesc,
  on    : (a, b) => Number(stateOf(b) !== 'off') - Number(stateOf(a) !== 'off') || byDateDesc(a, b),
  name  : (a, b) => a.name.localeCompare(b.name, 'fr', { sensitivity: 'base', numeric: true }),
};

interface Props {
  adminId        : number;
  includeDeleted : boolean;
}

type Playing = { title: string; url: string; stopped?: boolean; logo?: string };

const CreatorChannelsPanel: React.FC<Props> = ({ adminId, includeDeleted }) => {

  const [ channels, setChannels ] = useState<DashboardChannel[]>([]);
  const [ loading , setLoading  ] = useState(true);
  const [ error   , setError    ] = useState(false);
  const [ origin  , setOrigin   ] = useState<'all' | ChannelOrigin>('all');
  const [ state   , setState    ] = useState<'all' | 'on' | 'off' | 'locked'>('all');
  const [ universe, setUniverse ] = useState<string>('all');
  const [ query   , setQuery    ] = useState('');
  const [ sort    , setSort     ] = useState<Sort>('recent');
  const [ view    , setView     ] = useView(VIEW_KEY);
  const [ playing , setPlaying  ] = useState<Playing | null>(null);

  // Relevé des chaînes ; refait après une action super-admin pour que l'état vienne du serveur.
  const [ reload, setReload ] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (reload === 0) setLoading(true);
    setError(false);
    axios.get(API_URLS.getDashboardLatest, { params: { ownerId: adminId, only: 'channels', limit: 1000, ...(includeDeleted ? { deleted: 1 } : {}) } }).then(({ data }) => {
      if (cancelled) return;
      setChannels((data.channels ?? []).map((x: any): DashboardChannel => ({
        id: x.id, name: x.name || '', publicRef: x.publicRef || '', slug: x.slug || '',
        hasThumbnail: !!x.hasThumbnail, creationDate: x.creationDate ?? null, ephemeral: !!x.ephemeral,
        channelStatus: x.channelStatus || '', streamSource: x.streamSource || 'video', publicPortal: !!x.publicPortal,
        origin: x.origin || 'unknown', city: x.city ?? null, universe: x.universe ?? null,
        owner: x.owner || '', ownerEmail: x.ownerEmail || '',
        lock: x.lock ?? null, geo: x.geo ?? null, devices: [], deleted: !!x.deleted,
      })));
    }).catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [adminId, includeDeleted, reload]);

  // Mêmes actions super-admin que l'onglet Chaînes du Dashboard (éteindre, verrouiller, déverrouiller).
  const { authentication } = useTypedSelector((state: RootState) => state.authenticationReducer);
  const moderation = useCallback(async (run: (adminId: number) => ReturnType<typeof stopChannel>, done: string): Promise<ModerationOutcome> => {
    const me = authentication.admin?.id;
    if (!me) return { error: 'Compte non identifié : reconnectez-vous.' };
    const res = await run(me);
    setReload(n => n + 1);
    if (!res.ok) return { error: res.error };
    const mail = res.state.email;
    const mailText = !mail ? '' : mail.sent ? ` Un e-mail a été envoyé à ${mail.to}.` : ` L'e-mail n'a pas pu être envoyé${mail.to ? ` à ${mail.to}` : ''} : ${mail.error}.`;
    return { error: null, notice: done + mailText };
  }, [authentication.admin?.id]);
  const { moderate, notice, setNotice, dialogElement } = useChannelModeration(
    c => moderation(id => stopChannel(c.id, id), `« ${c.name} » est éteinte.`),
    (c, reason) => moderation(id => lockChannel(c.id, id, reason), `« ${c.name} » est verrouillée.`),
    (c, note) => moderation(id => unlockChannel(c.id, id, note), `« ${c.name} » est déverrouillée.`));

  const play = useCallback((c: DashboardChannel) => {
    if (!c.publicRef) return;
    if (!ON_STATUSES.includes(c.channelStatus)) setPlaying({ title: c.name, url: '', stopped: true, logo: c.hasThumbnail ? `${WWW_URLS.MediaServer}${c.publicRef}/images/logo.webp` : '' });
    else setPlaying({ title: c.name, url: `https://${WWW_DOMAIN}/${c.publicRef}/index.m3u8` });
  }, []);

  const universes = useMemo(() => {
    const count = new Map<string, number>();
    channels.forEach(c => { if (c.universe) count.set(c.universe, (count.get(c.universe) ?? 0) + 1); });
    return Array.from(count.entries()).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([u]) => u);
  }, [channels]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return channels.filter(c =>
      (origin === 'all' || c.origin === origin) &&
      (universe === 'all' || c.universe === universe) &&
      (state === 'all' || (state === 'locked' ? !!c.lock : state === 'on' ? stateOf(c) !== 'off' : stateOf(c) === 'off')) &&
      (!q || `${c.name} ${c.universe ?? ''}`.toLowerCase().includes(q))
    ).sort(COMPARE[sort]);
  }, [channels, origin, universe, state, query, sort]);

  const filtersOn = query.trim() !== '' || origin !== 'all' || state !== 'all' || universe !== 'all';
  const reset     = () => { setQuery(''); setOrigin('all'); setState('all'); setUniverse('all'); };

  if (loading) return <span>Chargement des chaînes…</span>;
  if (error)   return <div className={styles.empty}>Chargement impossible.</div>;
  if (channels.length === 0) return <div className={styles.empty}>Aucune chaîne.</div>;

  return (
    <div>
      <div className={styles.toolbar} style={{ marginBottom: 16 }}>
        <div className={extra.search}>
          <svg className={extra.searchIcon} width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="7" cy="7" r="5"/><path d="M11 11l4 4"/></svg>
          <input className={extra.searchInput} type="text" value={query} placeholder="Nom…" aria-label="Rechercher une chaîne"
                 onChange={e => setQuery(e.target.value)} />
          {query && <button type="button" className={extra.searchClear} onClick={() => setQuery('')} aria-label="Effacer la recherche">✕</button>}
        </div>
        <Segment label="Origine" value={origin} onChange={setOrigin}
                 choices={[ ['all', 'Toutes'], ['regie', 'Régie web'], ['ios', 'iPhone'], ['android', 'Android'] ] as const} />
        <Segment label="État" value={state} onChange={setState}
                 choices={[ ['all', 'Toutes'], ['on', 'Allumées'], ['off', 'Éteintes'], ['locked', 'Verrouillées'] ] as const} />
        {universes.length > 1 &&
          <Segment label="Univers" value={universe} onChange={setUniverse}
                   choices={[ ['all', 'Tous les univers'] as const, ...universes.map(u => [u, u] as const) ]} />}
        {filtersOn && <>
          <span className={extra.count} aria-live="polite"><strong>{plural(shown.length, 'chaîne')}</strong> sur {channels.length}</span>
          <button type="button" className={extra.reset} onClick={reset}>Réinitialiser</button>
        </>}
        <span className={styles.spacer}/>
        <label className={extra.sort}>Trier par
          <select value={sort} onChange={e => setSort(e.target.value as Sort)}>
            {SORTS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
        </label>
        <ViewToggle view={view} onChange={setView} />
      </div>

      {notice && (
        <div className={styles.notice} role="status">
          <span>{notice}</span>
          <button type="button" className={styles.secondary} onClick={() => setNotice('')}>Fermer</button>
        </div>
      )}

      {shown.length === 0 && <div className={styles.empty}>Aucune chaîne ne correspond.</div>}

      {view === 'cards' && shown.length > 0 && (
        <div className={styles.cards}>
          {shown.map(c => (
            <article key={c.id} className={`${isNew(c) ? `${styles.card} ${styles.cardNew}` : (stateOf(c) === 'live' ? `${styles.card} ${styles.cardLive}` : styles.card)} ${c.lock ? styles.cardLocked : ''}`}
                     style={c.deleted ? { opacity: 0.6 } : undefined}>
              <Thumb c={c} />
              <div className={styles.cardBody}>
                <span className={styles.cardName} title={c.name}>{c.name}</span>
                <div className={styles.chips}>
                  {c.deleted && <span>Supprimée</span>}
                  <span>{ORIGIN_LABEL[c.origin]}</span>
                  {placeOf(c) && <span>📍 {placeOf(c)}</span>}
                  {c.universe && <span>{c.universe}</span>}
                </div>
                <Actions c={c} onPlay={play} onModerate={moderate} />
              </div>
            </article>
          ))}
        </div>
      )}

      {view === 'list' && shown.length > 0 && (
        <div className={styles.tableBox}>
          <table className={styles.table} style={{ minWidth: 900 }}>
            <thead>
              <tr><th>Chaîne</th><th>Créée</th><th>Origine</th><th>Ville du créateur</th><th>Univers</th><th>État</th><th>Kleon.tv</th><th className={styles.right}>Actions</th></tr>
            </thead>
            <tbody>
              {shown.map(c => (
                <tr key={c.id} style={c.deleted ? { opacity: 0.6 } : undefined}>
                  <td><div className={styles.cellName}><span className={styles.miniThumb} style={{ background: tintOf(c.id) }}/>{c.name}{c.deleted ? ' (supprimée)' : ''}</div></td>
                  <td className={styles.nowrap}>{dayLabel(c.creationDate)} {hour(c.creationDate)}</td>
                  <td>{ORIGIN_LABEL[c.origin]}</td>
                  <td>{placeOf(c) || '—'}</td>
                  <td>{c.universe ?? '—'}</td>
                  <td><Badge state={stateOf(c)} fresh={isNew(c)} locked={!!c.lock} deleted={c.deleted} /></td>
                  <td className={c.publicPortal ? styles.visible : styles.muted}>{c.publicPortal ? 'Visible' : 'Non listée'}</td>
                  <td className={styles.right}><Actions c={c} onPlay={play} onModerate={moderate} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dialogElement}

      {playing && (
        <div className={dash.overlay} onClick={() => setPlaying(null)}>
          <div className={dash.modal} onClick={e => e.stopPropagation()}>
            <div className={dash.sectionHeader}>
              <span>{playing.stopped ? '' : 'Direct — '}{playing.title}</span>
              <button className={dash.refresh} onClick={() => setPlaying(null)} title="Fermer">✕</button>
            </div>
            {playing.url
              ? <VideoPlayer url={playing.url} />
              : <div className={dash.stopped}>
                  {playing.logo && <img src={playing.logo} alt={playing.title} className={dash.stoppedLogo} />}
                  <div>Est arrêtée</div>
                </div>}
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatorChannelsPanel;
